import { HttpClient } from '@angular/common/http';
import { EventEmitter, Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AUTH_GRANT_TYPE_ENUM, LoggedUserViewModel, TokenRequestViewModel } from '../../types/common';
import { SpinnerService } from './spinner.service';

@Injectable()
export class AuthService {
    static AuthKey = 'auth';

    private logoutEvent = new EventEmitter(true);
    private loginEvent = new EventEmitter<string>(true);

    logout$ = this.logoutEvent.asObservable();
    login$ = this.loginEvent.asObservable();

    constructor(
        private http: HttpClient,
        private spinnerService: SpinnerService,
        @Inject('BASE_URL') private baseUrl: string,
    ) {}

    // performs the login
    login(userid: string, password: string): Observable<boolean> {
        let requestToken = new TokenRequestViewModel();
        requestToken.UserID = userid;
        requestToken.Password = password;
        requestToken.GrantType = AUTH_GRANT_TYPE_ENUM.PASSWORD;
        requestToken.CASTicket = '';

        return this.signIn(requestToken);
    }

    // performs the login CAS
    authenticateWebCAS(ticket: string): Observable<boolean> {
        let requestToken = new TokenRequestViewModel();
        requestToken.UserID = '';
        requestToken.Password = '';
        requestToken.GrantType = AUTH_GRANT_TYPE_ENUM.CAS;

        requestToken.CASTicket = ticket;

        return this.signIn(requestToken);
    }

    // performs the login
    refreshToken(): Observable<boolean> {
        let requestToken = new TokenRequestViewModel();

        let user = this.getLoggedInUser();
        if (user?.XmlSignedId) {
            requestToken.GrantType = AUTH_GRANT_TYPE_ENUM.REFRESH_ACLAVE;
        } else {
            requestToken.GrantType = AUTH_GRANT_TYPE_ENUM.REFRESH_TOKEN;
            requestToken.CASTicket = '';
            requestToken.UserID = user?.NIF || '';
            requestToken.RefreshToken = user?.TokenResponse!.RefreshToken || '';

            if (!requestToken.UserID) {
                return of(false);
            }
        }

        return this.signIn(requestToken);
    }

    refreshTokenClave(): Observable<boolean> {
        const requestToken = new TokenRequestViewModel();
        requestToken.GrantType = AUTH_GRANT_TYPE_ENUM.REFRESH_ACLAVE;
        return this.signIn(requestToken);
    }

    private signIn(requestToken: TokenRequestViewModel): Observable<boolean> {
        if (!requestToken) {
            return of(false);
        }
        let url = this.baseUrl + 'api/core/auth';
        switch (requestToken.GrantType) {
            case AUTH_GRANT_TYPE_ENUM.REFRESH_TOKEN:
                url += '/RefreshToken';
                break;
            case AUTH_GRANT_TYPE_ENUM.REFRESH_ACLAVE:
                url += '/RefreshTokenClave';
                break;
            default:
                break;
        }

        return this.http.post<LoggedUserViewModel>(url, requestToken).pipe(
            map(res => {
                let token = res.TokenResponse && res.TokenResponse.Token;
                // if the token is there, login has been successful

                if (token) {
                    // store username and jwt token
                    this.setAuth(res);

                    // successful login
                    this.emitLogin(requestToken.GrantType);
                    return true;
                }

                // failed login
                return false;
            }),
            catchError((err: any) => {
                this.spinnerService.hideSpinner();
                this.emitLogout();
                throw err;
            }),
        );
    }

    parseJwt(token: string): any {
        let base64Url = token.split('.')[1];
        let base64 = base64Url.replace('-', '+').replace('_', '/');
        return JSON.parse(window.atob(base64));
    }

    // performs the logout
    emitLogout(): void {
        this.logoutEvent.emit();
    }

    emitLogin(grantType: string): void {
        this.loginEvent.emit(grantType);
    }

    // Persist auth into localStorage or removes it if a NULL argument is given
    setAuth(user: LoggedUserViewModel | null): boolean {
        if (user) {
            localStorage.setItem(AuthService.AuthKey, JSON.stringify(user));
        } else {
            localStorage.removeItem(AuthService.AuthKey);
        }
        return true;
    }

    // Retrieves the auth JSON object (or NULL if none)
    getLoggedInUser(): LoggedUserViewModel | null {
        let user: LoggedUserViewModel = new LoggedUserViewModel();
        let userJSON: LoggedUserViewModel;

        let i = localStorage.getItem(AuthService.AuthKey);
        if (i) {
            userJSON = JSON.parse(i);

            user.AccionCollection = userJSON.AccionCollection;
            user.TUsuarioPerfilCollection = userJSON.TUsuarioPerfilCollection;
            user.Apellidos = userJSON.Apellidos;
            user.Email = userJSON.Email;
            user.Id = userJSON.Id;
            user.NIF = userJSON.NIF;
            user.Nombre = userJSON.Nombre;
            user.TokenResponse = userJSON.TokenResponse;
            user.TwUsuarioCa = userJSON.TwUsuarioCa;
            user.TwUsuarioInspector = userJSON.TwUsuarioInspector;
            user.TwRegTecnicoEntidadAuditora = userJSON.TwRegTecnicoEntidadAuditora;
            user.XmlSignedId = userJSON.XmlSignedId;

            return user;
        } else {
            return null;
        }
    }

    // Returns TRUE if the user is logged in, FALSE otherwise.
    isLoggedIn(): boolean {
        return localStorage.getItem(AuthService.AuthKey) !== null;
    }
}
